import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  DestroyRef,
  effect,
  inject,
  signal
} from '@angular/core';
import { BatchResponse, Department, ListBatchesParams } from '../../models/sdk-interfaces';
import { BatchService } from '../../services/batch/batch.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';
import { FronteggAuthService } from '@frontegg/angular';
import { User } from '@frontegg/redux-store';
import { ActivatedRoute } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { SortMeta, PrimeTemplate, ConfirmationService } from 'primeng/api';
import { Tooltip } from 'primeng/tooltip';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../services/toast-messages/toast-messages.service';
import { AutoclassifierSdkService } from '../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { NgClass, DatePipe } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DocumentExpirationWarningComponent } from '../document-expiration-warning/document-expiration-warning.component';
import { isExpiringSoon } from '../document-expiration-warning/document-expiration.util';

interface BatchColumn {
  field: string;
  header: string;
  allowSort: boolean;
  value: (batch: BatchResponse) => string;
  visible?: () => boolean;
}

@Component({
  selector: 'app-batch-list',
  templateUrl: './batch-list.component.html',
  styleUrls: ['./batch-list.component.scss'],
  imports: [
    NgClass,
    Bind,
    TableModule,
    PrimeTemplate,
    Button,
    Tooltip,
    DatePipe,
    TranslatePipe,
    DocumentExpirationWarningComponent
  ],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchListComponent implements OnInit {
  private fronteggAuthService = inject(FronteggAuthService);
  private batchService = inject(BatchService);
  userPrivilegesService = inject(UserPrivilegesService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private featureFlagService = inject(FeatureFlagService);
  private datePipe = inject(DatePipe);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private confirmationService = inject(ConfirmationService);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);

  @Input() tenantId: string = '';
  @Output() openBatchEvent = new EventEmitter<string>();

  @ViewChild('tableContainer', { static: true }) tableContainer!: ElementRef;
  @ViewChild('dt') dt!: Table;

  user?: User;
  batches: BatchResponse[] = [];
  cols: BatchColumn[] = [];
  isLoading: boolean = false;
  offset: number = 0;
  count: number = 500;
  hasMoreData: boolean = true;
  canListBatches: boolean = false;
  canManageCompletedBatches: boolean = false;
  userDepartments: Department[] = [];
  allowedStatuses: string[] = [];

  filters: { [key: string]: string } = {};
  globalFilterValue: string = '';

  currentTab: string = 'available';
  openableBatchIds = new Set<string>();
  selectedBatches: BatchResponse[] = [];
  isDeleting = signal(false);

  private static readonly tabs = ['available', 'checked_out', 'completed'];
  private static readonly deleteConcurrency = 5;

  docExpirationUiEnabled = toSignal(
    this.featureFlagService.getFlag('feature-capture-doc-expiration-ui', false)
  );

  baseCols: BatchColumn[] = [
    { field: 'batchId', header: 'id', allowSort: false, value: batch => batch.batchId ?? '' },
    { field: 'name', header: 'batchName', allowSort: true, value: batch => batch.name ?? '' },
    {
      field: 'unprocessedDocs',
      header: 'batchTable.unprocessedDocs',
      allowSort: false,
      value: batch => this.getUnprocessedDocs(batch),
      visible: () => this.docExpirationUiEnabled()
    },
    {
      field: 'processedDocs',
      header: 'batchTable.processedDocs',
      allowSort: false,
      value: batch => this.getProcessedDocs(batch)
    },
    {
      field: 'expiredDocs',
      header: 'batchTable.expiredDocs',
      allowSort: false,
      value: batch => this.getExpiredDocs(batch),
      visible: () => this.docExpirationUiEnabled()
    },
    {
      field: 'createdAt',
      header: 'creationDate',
      allowSort: true,
      value: batch => this.datePipe.transform(batch.createdAt, 'MM/dd/yyyy hh:mm a') ?? ''
    },
    {
      field: 'documentsExpiring',
      header: 'documentsExpiring',
      allowSort: true,
      value: batch => this.getDocumentsExpiringLabel(batch),
      visible: () => this.docExpirationUiEnabled()
    }
  ];

  // TODO: Remove with feature flag removal on columns
  // Check signal and updates the column visibility if FF changes.
  private readonly syncColsWithVisibility = effect(() => {
    this.baseCols.forEach(col => col.visible?.());
    this.updateCols();
  });

  /**
   * Generates the URL for a given batch.
   * @param batch The batch object for which to generate the URL.
   */
  getBatchUrl(batch: BatchResponse): string {
    return `webcapture/batches/${batch.batchId}`;
  }

  initialSort: SortMeta[] = [
    { field: 'createdAt', order: 1 } // 1 for ascending, -1 for descending
  ];

  /**
   * Returns the sort order for a given field based on the table's current single-column sort state.
   * @param {string} field - The field name (column key) to check the sort order for.
   * @returns {number} The sort order: 1 for ascending.
   */
  getSortOrder(field: string): number {
    if (!this.dt || !this.dt.multiSortMeta) return 0;

    const sortMeta = this.dt.multiSortMeta.find((meta: any) => meta.field === field);
    if (sortMeta) {
      return sortMeta.order;
    }
    return 0;
  }

  /**
   * Called once after component initialization.
   */
  ngOnInit(): void {
    this.initializeComponent();
    this.loadUserAndPrivileges();
  }

  /**
   * Restores the tenant ID from local storage and initializes the visible columns.
   */
  private initializeComponent(): void {
    this.tenantId = localStorage.getItem('tenantId') || '';
    this.updateCols();
  }

  /**
   * Loads the current user and their privileges, then fetches the list of batches
   */
  private loadUserAndPrivileges(): void {
    const organization = localStorage.getItem('vanityName');

    if (!this.tenantId || !organization) {
      return;
    }

    this.fronteggAuthService.authState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(authState => {
        if (authState.user?.accessToken) {
          this.user = authState.user;
        }
      });

    this.userPrivilegesService
      .getDepartmentsByPrivilege('LIST_BATCHES')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(departments => {
        this.handleDepartmentsLoaded(departments);
      });
  }

  /**
   * Handles the loaded departments and sets up the component state accordingly.
   * @param departments The list of departments the user has access to, along with their allowed statuses.
   */
  private handleDepartmentsLoaded(departments: Department[]): void {
    this.userDepartments = departments;
    this.extractAllowedStatuses(departments);

    const tabParam = this.route.snapshot.queryParams['tab'];

    if (tabParam && BatchListComponent.tabs.includes(tabParam)) {
      this.filterByTab(tabParam);
    } else if (this.userDepartments.length > 0) {
      this.loadData();
    }
  }

  /**
   * Extracts the allowed batch statuses from the user's departments and updates the component state.
   * @param departments The list of departments the user has access to, along with their allowed statuses.
   */
  private extractAllowedStatuses(departments: Department[]): void {
    this.allowedStatuses = departments
      .flatMap(dept => dept.status || [])
      .filter((status, index, self) => self.indexOf(status) === index);
  }

  /**
   * Filters the batch list based on the selected tab and updates the displayed columns accordingly.
   * @param tab The selected tab, which is also the batch status to filter by. Expected values are 'available', 'checked_out', or 'completed'.
   */
  filterByTab(tab: string): void {
    this.currentTab = tab;
    this.openableBatchIds = new Set<string>();
    this.selectedBatches = [];
    this.updateCols();

    this.resetData(tab);
  }

  /**
   * Updates the columns to display based on the current tab.
   */
  updateCols(): void {
    this.cols = this.baseCols.filter(col => col.visible?.() ?? true);
  }

  /**
   * Resets the batch data and reloads it based on the provided status.
   * @param status The new status filter for the batch data.
   */
  resetData(status?: string): void {
    this.batches = [];
    this.offset = 0;
    this.hasMoreData = true;
    this.loadData(status);
  }

  /**
   * Loads the batch data based on the provided status.
   * @param status The status filter for the batch data.
   */
  loadData(status?: string): void {
    if (this.isLoading || !this.hasMoreData) {
      return;
    }

    this.isLoading = true;

    const params: ListBatchesParams = {
      offset: this.offset,
      count: this.count,
      status: status || this.currentTab
    };

    this.batchService
      .listBatches(params)
      .then(response => {
        const newBatches = response.items;

        if (newBatches.length < this.count) {
          this.hasMoreData = false; // No more data to load
        }

        this.batches = this.filterBatchesByDepartment(
          [...this.batches, ...newBatches],
          this.userDepartments
        );

        // Expose the earliest expiration date as a top-level field so the table can sort the
        // "Documents Expiring" column by it (ISO date strings sort chronologically).
        this.batches.forEach(batch => {
          (batch as BatchResponse & { documentsExpiring: string | null }).documentsExpiring =
            batch.statistics?.earliestExpirationDate ?? null;
        });

        this.precomputeOpenableBatches();
        this.offset += this.count;
        this.isLoading = false;
        this.cdr.detectChanges();

        setTimeout(() => {
          const element = this.tableContainer.nativeElement;
          // If there is still more data AND
          //  the table is not scrollable => load more
          if (element.scrollHeight <= element.clientHeight && this.hasMoreData) {
            this.loadData(status); // recursively fetch the next chunk
          }
        });
      })
      .catch(error => {
        console.error('Error fetching batches:', error);
        this.isLoading = false;
      });
  }

  /**
   * Filters the batch list based on the user's department privileges, ensuring that only batches matching the allowed departments and statuses are displayed.
   * @param batches The list of batches to filter.
   * @param departments The list of departments the user has access to, along with their allowed statuses.
   */
  filterBatchesByDepartment(batches: BatchResponse[], departments: Department[]): BatchResponse[] {
    const privileges = this.userPrivilegesService.getCurrentPrivileges();
    if (!privileges) {
      return [];
    }

    const listBatchesPrivilege = privileges.resourcePrivileges.find(
      p => p.operationName === 'LIST_BATCHES'
    );
    if (!listBatchesPrivilege) {
      return [];
    }

    // Filter out only the batches that match the privilege’s department & status
    return batches.filter(batch => {
      const dep = listBatchesPrivilege.context.departments.find(d => d.code === batch.department);
      if (!dep) {
        return false;
      }

      if (dep.status && dep.status.length > 0) {
        return dep.status.includes(batch.status!);
      } else {
        return false;
      }
    });
  }

  /**
   * Returns the number of documents in the batch that have not yet been processed.
   * @param batch The batch whose document statistics are read.
   */
  getUnprocessedDocs(batch: BatchResponse): string {
    const documentCount = batch.statistics?.documentCount || 0;
    const unprocessedDocs = batch.statistics?.unprocessedDocumentCount || 0;
    return `${unprocessedDocs}/${documentCount}`;
  }

  /**
   * Returns the number of documents in the batch that have been processed, derived from the
   * total document count minus the unprocessed count.
   * @param batch The batch whose document statistics are read.
   */
  getProcessedDocs(batch: BatchResponse): string {
    const documentCount = batch.statistics?.documentCount || 0;
    const unprocessedDocumentCount = batch.statistics?.unprocessedDocumentCount || 0;
    const processedDocCount = documentCount - unprocessedDocumentCount;
    return `${processedDocCount}/${documentCount}`;
  }

  /**
   * Returns the number of documents in the batch that have already expired.
   * @param batch The batch whose document statistics are read.
   */
  getExpiredDocs(batch: BatchResponse): string {
    const documentCount = batch.statistics?.documentCount || 0;
    const expiredDocs = batch.statistics?.expiredDocumentCount || 0;
    return `${expiredDocs}/${documentCount}`;
  }

  /**
   * Returns the Font Awesome sort-direction icon class for a column, or an empty string when unsorted.
   * @param field The column field to resolve the sort icon for.
   */
  getSortIconClass(field: string): string {
    const order = this.getSortOrder(field);
    if (order === 1) {
      return 'fa-sort-up';
    }
    if (order === -1) {
      return 'fa-sort-down';
    }
    return '';
  }

  /**
   * Returns the "Documents Expiring" cell label ("MM/dd/yyyy (N documents)"), or an empty string when the batch has no expiration date.
   * @param batch The batch whose expiration statistics are formatted.
   */
  getDocumentsExpiringLabel(batch: BatchResponse): string {
    const expirationDate = batch.statistics?.earliestExpirationDate;
    if (!expirationDate) {
      return '';
    }

    const formattedDate = this.datePipe.transform(expirationDate, 'MM/dd/yyyy', 'UTC');
    const count = batch.statistics?.expiringDocumentCount ?? 0;
    const unit = this.translate.instant('documentsExpiringUnit');
    return `${formattedDate} (${count} ${unit})`;
  }

  /**
   * Whether the batch's earliest-expiring document is within a week of expiring,
   * used to apply the row's warning background.
   * @param batch The batch whose expiration statistics are checked.
   */
  isExpiringSoon(batch: BatchResponse): boolean {
    return (
      this.docExpirationUiEnabled() && isExpiringSoon(batch.statistics?.earliestExpirationDate)
    );
  }

  /**
   * Pre-computes which batches can be opened and caches the result.
   */
  private precomputeOpenableBatches(): void {
    this.openableBatchIds = new Set<string>();
    if (this.currentTab === 'checked_out') return;

    const privileges = this.userPrivilegesService.getCurrentPrivileges();
    if (!privileges) return;

    const privilege = privileges.resourcePrivileges.find(p => p.operationName === 'VIEW_BATCH');
    if (!privilege) return;

    this.batches.forEach(batch => {
      const dep = privilege.context.departments.find(d => d.code === batch.department);
      if (!dep) return;
      if (dep.status?.length) {
        if (dep.status.includes(batch.status!)) this.openableBatchIds.add(batch.batchId!);
      } else {
        this.openableBatchIds.add(batch.batchId!);
      }
    });
  }

  /**
   * Opens a batch if the current user is allowed to open it.
   * @param event The click event triggered when a user attempts to open a batch.
   * @param batch The batch object that the user is attempting to open.
   */
  openBatch(event: Event, batch: BatchResponse): void {
    event.preventDefault();
    if (this.openableBatchIds.has(batch.batchId!)) {
      this.openBatchEvent.emit(batch.batchId);
    }
  }

  /**
   * Determines whether a batch can be opened based on pre-computed openable batch IDs.
   * @param batch The batch object for which to determine if it can be opened.
   */
  canOpenBatch(batch: BatchResponse): boolean {
    return this.openableBatchIds.has(batch.batchId!);
  }

  /**
   * Whether the current tab allows selecting batches for deletion.
   */
  get isSelectionEnabled(): boolean {
    return this.currentTab === 'available' || this.currentTab === 'completed';
  }

  /**
   * Determines whether the current user can delete a batch, based on the DELETE_BATCH privilege
   * for the batch's department and status.
   * @param batch The batch to check.
   */
  canDeleteBatch(batch: BatchResponse): boolean {
    const privileges = this.userPrivilegesService.getCurrentPrivileges();
    const privilege = privileges?.resourcePrivileges.find(p => p.operationName === 'DELETE_BATCH');
    const dep = privilege?.context.departments.find(d => d.code === batch.department);
    if (!dep) return false;
    return !dep.status?.length || dep.status.includes(batch.status!);
  }

  /**
   * Whether the user can delete every currently selected batch.
   */
  get canDeleteSelected(): boolean {
    return this.selectedBatches.every(batch => this.canDeleteBatch(batch));
  }

  /**
   * Opens the confirmation dialog for deleting the selected batches.
   */
  confirmDeleteSelected(): void {
    if (!this.selectedBatches.length || !this.canDeleteSelected) return;

    const params = { count: this.selectedBatches.length };
    this.confirmationService.confirm({
      message:
        `<p class="dialog-content">${this.translate.instant('confirmDeleteBatches', params)}</p>` +
        `<p class="confirm-message">${this.translate.instant('confirmDeleteBatchesProceed', params)}</p>`,
      key: 'deleteBatchesConfirmDialog',
      accept: () => {
        this.deleteSelectedBatches();
      }
    });
  }

  /**
   * Deletes the selected batches and their documents, removes the deleted ones from the table,
   * and reports the outcome in toasts.
   */
  private async deleteSelectedBatches(): Promise<void> {
    const toDelete = [...this.selectedBatches];
    this.isDeleting.set(true);

    const results: PromiseSettledResult<void>[] = [];
    for (let i = 0; i < toDelete.length; i += BatchListComponent.deleteConcurrency) {
      const chunk = toDelete.slice(i, i + BatchListComponent.deleteConcurrency);
      results.push(
        ...(await Promise.allSettled(chunk.map(batch => this.deleteBatchAndDocuments(batch))))
      );
    }

    const deletedIds = new Set<string>();
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        deletedIds.add(toDelete[index].batchId!);
      } else {
        console.error('Error deleting batch:', toDelete[index].batchId, result.reason);
      }
    });

    this.batches = this.batches.filter(batch => !deletedIds.has(batch.batchId!));
    this.selectedBatches = this.selectedBatches.filter(batch => !deletedIds.has(batch.batchId!));
    this.isDeleting.set(false);

    const failed = toDelete.length - deletedIds.size;
    if (deletedIds.size) {
      this.toastService.success(
        'success',
        this.translate.instant('batchTable.deleteBatchesSuccess', {
          success: deletedIds.size,
          total: toDelete.length
        })
      );
    }
    if (failed) {
      this.toastService.error(
        'batchTable.deleteFailed',
        this.translate.instant('batchTable.deleteBatchesFailed', {
          failed,
          total: toDelete.length
        })
      );
    }
  }

  /**
   * Deletes a batch and then removes its documents from storage. Rejects when the batch has been
   * checked out since the list was loaded.
   * @param batch The batch to delete.
   */
  private async deleteBatchAndDocuments(batch: BatchResponse): Promise<void> {
    const batchId = batch.batchId!;
    const current = await this.batchService.getBatch(batchId);
    if (current.status === 'checked_out') {
      throw new Error(`Batch ${batchId} is checked out`);
    }

    const { items } = await this.batchService.getBatchDocuments(batchId);
    await this.batchService.deleteBatch(batchId);

    const authToken = this.authService.getAuthToken();
    await Promise.allSettled(
      items.map(doc =>
        this.autoclassifierSdkService.documentManagementSdk.deleteDocument(
          this.tenantId,
          doc.documentId,
          authToken
        )
      )
    );
  }

  /**
   * Checks in a batch.
   * @param batchId The ID of the batch to check in.
   */
  checkInBatch(batchId: string): void {
    this.batchService.checkinBatch(batchId).then(() => {
      this.resetData();
    });
  }

  /**
   * Determines whether the current user can check in a batch.
   * @param batch The batch to check.
   */
  canCheckInBatch(batch: BatchResponse): boolean {
    const privileges = this.userPrivilegesService.getCurrentPrivileges();
    if (!privileges) return false;
    return !!privileges.resourcePrivileges.find(
      p =>
        p.operationName === 'OVERRIDE_CHECKED_OUT_BATCH' &&
        p.context.departments.some(d => d.code === batch.department)
    );
  }
}
